import { useParams } from 'react-router-dom';
import { SUPPORTED_LANG_CODES, DEFAULT_LANGUAGE } from 'i18n/config';

/**
 * Pure function to format a path for the given language.
 * Follows the "prefix-except-default" routing model:
 * - Default language (e.g. 'en') has NO prefix: '/affiliate', '/'
 * - Non-default language (e.g. 'ru') has prefix: '/ru/affiliate', '/ru'
 * External URLs ('http://', 'https://', 'mailto:', 'tel:', '#') are left unchanged.
 */
export const getLocalizedPath = (path: string, lang: string = DEFAULT_LANGUAGE): string => {
  if (!path) return lang === DEFAULT_LANGUAGE ? '/' : `/${lang}`;

  // Keep external, protocol, or hash links unchanged
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('mailto:') ||
    path.startsWith('tel:') ||
    path.startsWith('#')
  ) {
    return path;
  }

  // Standalone routes that shouldn't have lang prefix (e.g. Google OAuth redirect)
  if (path.startsWith('/auth/google/callback')) {
    return path;
  }

  // Separate hash and search query params so only the pathname is modified
  const [pathnameAndQuery, hash] = path.split('#');
  const [rawPathname, search] = pathnameAndQuery.split('?');
  const searchPart = search !== undefined ? `?${search}` : '';
  const hashPart = hash !== undefined ? `#${hash}` : '';

  // Normalize path with leading slash
  const cleanPath = rawPathname.startsWith('/') ? rawPathname : `/${rawPathname}`;

  // Strip any existing language prefix (supported languages or any 2-3 letter code like 'kz', 'en', 'ru')
  const segments = cleanPath.split('/').filter(Boolean);
  let unlocalizedPath = cleanPath;
  if (
    segments.length > 0 &&
    ((SUPPORTED_LANG_CODES as readonly string[]).includes(segments[0]) ||
      /^[a-z]{2,3}(-[a-z]{2})?$/i.test(segments[0]))
  ) {
    const rest = segments.slice(1).join('/');
    unlocalizedPath = rest ? `/${rest}` : '/';
  }

  let resultPath: string;
  if (lang === DEFAULT_LANGUAGE) {
    resultPath = unlocalizedPath;
  } else if (unlocalizedPath === '/') {
    resultPath = `/${lang}`;
  } else {
    resultPath = `/${lang}${unlocalizedPath}`;
  }

  return `${resultPath}${searchPart}${hashPart}`;
};

/**
 * Hook to retrieve the current active language from route params or fallback to default.
 */
export const useCurrentLanguage = (): string => {
  const { lang } = useParams<{ lang?: string }>();

  if (lang && (SUPPORTED_LANG_CODES as readonly string[]).includes(lang)) {
    return lang;
  }

  return DEFAULT_LANGUAGE;
};

/**
 * Strips the language prefix from a pathname to get the unlocalized base path.
 * e.g. '/ru/casino/categories/slots' -> '/casino/categories/slots'
 *      '/kz/casino' -> '/casino'
 *      '/en/affiliate' -> '/affiliate'
 *      '/en' -> '/'
 *      '/kz' -> '/'
 */
export const getUnlocalizedPath = (pathname: string): string => {
  if (!pathname) return '/';

  const [pathnameAndQuery, hash] = pathname.split('#');
  const [rawPathname, search] = pathnameAndQuery.split('?');
  const searchPart = search !== undefined ? `?${search}` : '';
  const hashPart = hash !== undefined ? `#${hash}` : '';

  const cleanPath = rawPathname.startsWith('/') ? rawPathname : `/${rawPathname}`;
  const segments = cleanPath.split('/').filter(Boolean);

  let unlocalizedPath = cleanPath;
  if (
    segments.length > 0 &&
    ((SUPPORTED_LANG_CODES as readonly string[]).includes(segments[0]) ||
      /^[a-z]{2,3}(-[a-z]{2})?$/i.test(segments[0]))
  ) {
    const rest = segments.slice(1).join('/');
    unlocalizedPath = rest ? `/${rest}` : '/';
  }

  return `${unlocalizedPath}${searchPart}${hashPart}`;
};
